import type {
  IMessage,
  WelcomePayload,
  CollectPayload,
  GhostMovePayload,
  CollisionPayload,
  RespawnPayload,
  RocketPayload,
  EchoPayload,
  MovePayload,
  LeavePayload,
  JoinPayload,
} from "@shared/types";
import { showNotification } from "./ui";
import { scene } from "./core";
import { createPlayerMesh } from "./models/playerModel";
import { createRocketEffect } from "./models/rocketModel";
import { destroyWall } from "./sceneObjects";
import {
  getGameState,
  getLocalPlayerId,
  getLocalPlayer,
  getPlayerMeshes,
  getGhostMeshes,
  getDotMeshes,
  getWallMeshes,
  getRocketMeshes,
  setGameState,
  setLocalPlayerId,
  setLocalPlayer,
} from "./gameState";
import { initializeGameWorld } from "./world";
import { updateGhostEyes, resetMeshMaterial } from "./utils";
import * as THREE from "three";
import { VISUAL_CONFIG } from "@shared/config";
import { updatePlayerPosition, handleServerCorrection } from "./movement";
import { flashScoreElement } from "./ui-updates";
import { handleCollectEffect } from "./effects";

export function handleWelcomeMessage(
  message: IMessage,
  disconnectedSince: number | null
): void {
  if (!message.payload) return;
  const welcomePayload = message.payload as WelcomePayload;

  setGameState(null);
  setLocalPlayerId(null);
  setLocalPlayer(null);

  setGameState(welcomePayload.gameState);

  const playerIds = Object.keys(welcomePayload.gameState.players);
  if (playerIds.length > 0) {
    const myPlayerId = playerIds[playerIds.length - 1];
    setLocalPlayerId(myPlayerId);
    setLocalPlayer(welcomePayload.gameState.players[myPlayerId]);
    console.log("Joined game as player", myPlayerId);
  }

  initializeGameWorld(welcomePayload.gameState);

  const loadingElement = document.getElementById("loading");
  if (loadingElement) {
    loadingElement.style.display = "none";
  }

  if (disconnectedSince) {
    const downtime = Math.round((Date.now() - disconnectedSince) / 1000);
    showNotification(
      `Reconnected successfully after ${downtime} seconds!`,
      "info"
    );
  }
}

export function handleJoinMessage(message: IMessage): void {
  const gameState = getGameState();
  if (!message.payload || !gameState) return;

  const { player } = message.payload as JoinPayload;
  gameState.players[player.id] = player;
  const meshes = getPlayerMeshes();

  if (!meshes[player.id]) {
    meshes[player.id] = createPlayerMesh(player);
    showNotification(`New player joined - Team ${player.team}`, "info");
  }
}

export function handleLeaveMessage(message: IMessage): void {
  const gameState = getGameState();
  if (!message.payload || !gameState) return;

  const { playerId } = message.payload as LeavePayload;
  delete gameState.players[playerId];
  const pMeshes = getPlayerMeshes();

  if (pMeshes[playerId]) {
    scene.remove(pMeshes[playerId]);
    delete pMeshes[playerId];
  }
  console.log("Player left:", playerId);
}

export function handleMoveMessage(message: IMessage): void {
  if (!message.payload) return;

  const { playerId, x, y, z, inputSequence } = message.payload as MovePayload;
  const localId = getLocalPlayerId();
  const localPlayer = getLocalPlayer();

  updatePlayerPosition(playerId, x, y, z);

  if (playerId === localId && localPlayer) {
    handleServerCorrection(localId, localPlayer, x, y, z, inputSequence);
  }
}

export function handleGhostMoveMessage(message: IMessage): void {
  const gameState = getGameState();
  if (!gameState || !message.payload) return;

  const payload = message.payload as GhostMovePayload;
  if (!gameState.ghosts[payload.ghostId]) return;

  const previousX = gameState.ghosts[payload.ghostId].x;
  const previousY = gameState.ghosts[payload.ghostId].y;

  gameState.ghosts[payload.ghostId].x = payload.x;
  gameState.ghosts[payload.ghostId].y = payload.y;
  gameState.ghosts[payload.ghostId].z = payload.z;
  gameState.ghosts[payload.ghostId].targetId = payload.targetId;

  const gMeshes = getGhostMeshes();
  if (gMeshes[payload.ghostId]) {
    gMeshes[payload.ghostId].position.set(payload.x, 0.4, payload.y);

    if (
      gMeshes[payload.ghostId].children &&
      gMeshes[payload.ghostId].children.length >= 6
    ) {
      const leftPupil = gMeshes[payload.ghostId].children[4];
      const rightPupil = gMeshes[payload.ghostId].children[5];
      const dx = payload.x - previousX;
      const dy = payload.y - previousY;
      updateGhostEyes(leftPupil, rightPupil, dx, dy);
    }
  }
}

export function handleCollisionMessage(message: IMessage): void {
  const gameState = getGameState();
  const localId = getLocalPlayerId();
  if (!gameState || !message.payload) return;

  const { playerId, ghostId } = message.payload as CollisionPayload;

  if (gameState.players[playerId]) {
    gameState.players[playerId].isRespawning = true;

    if (playerId === localId) {
      setLocalPlayer(gameState.players[playerId]);
    }

    const playerMeshes = getPlayerMeshes();
    const ghostMeshes = getGhostMeshes();
    if (playerMeshes[playerId] && ghostMeshes[ghostId]) {
      import("./effects").then(
        ({ createCollisionEffect, applyRespawnEffect }) => {
          createCollisionEffect(
            playerMeshes[playerId].position.clone(),
            ghostMeshes[ghostId].position.clone()
          );
          applyRespawnEffect(playerId, playerMeshes[playerId]);
        }
      );
    }

    if (playerId === localId) {
      showNotification("You were caught by a ghost! Respawning...", "warning");
    } else {
      showNotification(`Player ${playerId.substring(0, 6)}... caught!`, "info");
    }
  }
}

export function handleRespawnMessage(message: IMessage): void {
  const gameState = getGameState();
  const localId = getLocalPlayerId();
  const localPlayer = getLocalPlayer();
  if (!gameState || !message.payload) return;

  const { playerId, x, y, z } = message.payload as RespawnPayload;

  if (gameState.players[playerId]) {
    gameState.players[playerId].x = x;
    gameState.players[playerId].y = y;
    gameState.players[playerId].z = z;
    gameState.players[playerId].isRespawning = false;

    const pMeshes = getPlayerMeshes();
    if (!pMeshes[playerId]) {
      pMeshes[playerId] = createPlayerMesh(gameState.players[playerId]);
    } else {
      pMeshes[playerId].position.set(x, 0.4, y);
      resetMeshMaterial(pMeshes[playerId]);
    }

    if (playerId === localId && localPlayer) {
      localPlayer.x = x;
      localPlayer.y = y;
      localPlayer.z = z;
      localPlayer.isRespawning = false;
      showNotification("Respawned! Watch out for ghosts.", "info");
      // Removed: previousPosition = pMeshes[playerId].position.clone();
    }
  }
}

export function handleCollectMessage(message: IMessage): void {
  if (!message.payload) return;

  const payload = message.payload as CollectPayload;
  const gameState = getGameState();
  const localId = getLocalPlayerId();

  if (!gameState) return;
  if (!payload.cellId) return;

  const [x, y, z] = payload.cellId.split(",").map(Number);
  const cell = gameState.maze.find((c) => c.x === x && c.y === y && c.z === z);

  if (cell) {
    // Update game state
    cell.hasDot = false;
    gameState.dotsRemaining = payload.dotsRemaining;

    // Update visual representation by removing the dot
    const dotMeshes = getDotMeshes();
    for (let i = dotMeshes.length - 1; i >= 0; i--) {
      const dot = dotMeshes[i];
      const dotX = Math.floor(dot.position.x);
      const dotZ = Math.floor(dot.position.z);
      if (dotX === x && dotZ === y) {
        // Create visual effect at the dot's position
        handleCollectEffect(dot.position.clone());
        // Remove the dot from the scene
        scene.remove(dot);
        // Remove from the array
        dotMeshes.splice(i, 1);
        break;
      }
    }

    // Update player scores locally to keep UI in sync
    if (gameState.players[payload.playerId]) {
      gameState.players[payload.playerId].score += payload.points;
      const team = gameState.players[payload.playerId].team;
      gameState.teamScores[team] += payload.points;

      // Flash score if it's the local player
      if (payload.playerId === localId) {
        flashScoreElement();
      }
    }
  }
}

export function handleRocketMessage(message: IMessage): void {
  const gameState = getGameState();
  const localId = getLocalPlayerId();
  const localPlayer = getLocalPlayer();
  if (!gameState || !message.payload) return;

  const rocketPayload = message.payload as RocketPayload;
  const { playerId } = rocketPayload;

  if (rocketPayload.collected) {
    const rockets = getRocketMeshes();
    const player = gameState.players[playerId];
    if (player) {
      const cellX = Math.floor(player.x);
      const cellY = Math.floor(player.y);
      const targetX = cellX + 0.5;
      const targetZ = cellY + 0.5;
      for (let i = rockets.length - 1; i >= 0; i--) {
        const rocket = rockets[i];
        if (
          Math.abs(rocket.position.x - targetX) < 0.1 &&
          Math.abs(rocket.position.z - targetZ) < 0.1
        ) {
          if (rocket.parent) {
            rocket.parent.remove(rocket);
          }
          rockets.splice(i, 1);
          break;
        }
      }
    }

    if (gameState.players[playerId]) {
      gameState.players[playerId].hasRocket = true;
      const playerMeshes = getPlayerMeshes();
      const playerMesh = playerMeshes[playerId];
      if (playerMesh) {
        const rocketIndicator = playerMesh.children.find(
          (child) => child.name === "rocketIndicator"
        );
        if (!rocketIndicator) {
          const rocketGeometry = new THREE.ConeGeometry(0.12, 0.25, 12);
          const rocketMat = new THREE.MeshLambertMaterial({
            color: VISUAL_CONFIG.COLORS.ROCKET,
            emissive: VISUAL_CONFIG.COLORS.ROCKET_EMISSIVE,
            emissiveIntensity: 0.7,
          });
          const rocketMesh = new THREE.Mesh(rocketGeometry, rocketMat);
          rocketMesh.position.set(0, -0.3, 0);
          rocketMesh.rotation.x = Math.PI;
          rocketMesh.name = "rocketIndicator";
          playerMesh.add(rocketMesh);
        }
      }
      if (playerId === localId && localPlayer) {
        localPlayer.hasRocket = true;
        showNotification(
          "Rocket collected! Press SPACE to destroy a wall.",
          "info"
        );
      }
    }
  } else if (
    rocketPayload.wallX !== undefined &&
    rocketPayload.wallY !== undefined &&
    rocketPayload.direction !== undefined
  ) {
    const {
      wallX,
      wallY,
      direction,
      adjacentWallX,
      adjacentWallY,
      adjacentWallDirection,
    } = rocketPayload;

    const cell = gameState.maze.find((c) => c.x === wallX && c.y === wallY);
    if (cell) {
      cell.walls[direction] = false;
    }
    if (
      adjacentWallX !== undefined &&
      adjacentWallY !== undefined &&
      adjacentWallDirection !== undefined
    ) {
      const adjCell = gameState.maze.find(
        (c) => c.x === adjacentWallX && c.y === adjacentWallY
      );
      if (adjCell) {
        adjCell.walls[adjacentWallDirection] = false;
      }
    }
    const wallMeshes = getWallMeshes();
    destroyWall(wallX, wallY, direction, wallMeshes);
    if (
      adjacentWallX !== undefined &&
      adjacentWallY !== undefined &&
      adjacentWallDirection !== undefined
    ) {
      destroyWall(
        adjacentWallX,
        adjacentWallY,
        adjacentWallDirection,
        wallMeshes
      );
    }
    if (gameState.players[playerId]) {
      const p = gameState.players[playerId];
      createRocketEffect(p.x, p.y, direction);
    }
    const playerMeshes = getPlayerMeshes();
    const playerMesh = playerMeshes[playerId];
    if (playerMesh) {
      const rocketIndicator = playerMesh.children.find(
        (child) => child.name === "rocketIndicator"
      );
      if (rocketIndicator) {
        playerMesh.remove(rocketIndicator);
      }
    }
    if (playerId === localId && localPlayer) {
      localPlayer.hasRocket = false;
      showNotification("Your rocket destroyed a wall!", "info");
    } else {
      const pTeam = gameState.players[playerId]?.team;
      showNotification(
        `Team ${pTeam} used a rocket to destroy a wall!`,
        "info"
      );
    }
  }
}

export function handleEchoMessage(message: IMessage): void {
  if (!message.payload) return;

  const payload = message.payload as EchoPayload;
  if (!payload.message) return;

  const type = payload.isWarning
    ? "warning"
    : payload.isGlobal
    ? "global"
    : "info";
  showNotification(payload.message, type);
}

export function handleRoundEndMessage(_message: IMessage): void {
  const gameState = getGameState();
  if (!gameState) return;

  showNotification("Round ended! Starting new round...", "global");

  let winningTeam = "";
  if (gameState.teamScores.A > gameState.teamScores.B) {
    winningTeam = "Team A";
  } else if (gameState.teamScores.B > gameState.teamScores.A) {
    winningTeam = "Team B";
  } else {
    winningTeam = "It's a tie!";
  }

  showNotification(
    `Round over! ${winningTeam} wins with ${Math.max(
      gameState.teamScores.A,
      gameState.teamScores.B
    )} points!`,
    "global"
  );
}
